// Encomenda por TURNO ou SÓ PELA DATA (12/10/2026, aprovado pelo Pedro — pedido da SL Personalizados): o produto
// por encomenda pede o horário (padrão, como sempre), o turno (manhã, tarde, noite) ou só a data. As regras ficam
// aqui para o link, o PDV e o servidor contarem igual.
//
// - Turno: horário de cada um é da LOJA. Sem horário próprio salvo (stores.order_shift_hours null), sai do horário de
//   funcionamento (Pedro, 12/10/2026: «abre às 07:00 e fecha às 18:00 → Manhã 07:00 às 12:00, Tarde 12:00 às 18:00,
//   Noite desligado»), e turno em que a loja não abre vem desligado; sem horário de funcionamento, o padrão 5h–12h,
//   12h–18h, a partir das 18h. A loja pode mudar e desligar turno (Configurações → Produtos).
// - Antecedência em horas conta até o COMEÇO do turno; em "só a data", até o começo do dia.
// - "Uma encomenda por horário" vira uma por turno; em "só a data" não vale.
// - "Data única no carrinho" com modos diferentes: vale o mais detalhado (horário > turno > data).

export type Turno = "manha" | "tarde" | "noite";
export type ModoEncomenda = "horario" | "turno" | "data";
export type FaixaDoTurno = { start: string; end: string | null; ligado: boolean };
export type HorarioDosTurnos = Record<Turno, FaixaDoTurno>;

export const TURNOS: readonly Turno[] = ["manha", "tarde", "noite"];

export const HORARIO_DOS_TURNOS_PADRAO: HorarioDosTurnos = {
  // Manhã às 05:00 (Pedro, 12/10/2026: «Ai o padrão muda de 08:00 para 05:00 da manhã»). Não confundir com o começo
  // do dia do «Apenas o Dia» (inicioDoDia, 8h) nem com a grade do «Dia e Hora» (8h–20h), que não mudaram.
  manha: { start: "05:00", end: "12:00", ligado: true },
  tarde: { start: "12:00", end: "18:00", ligado: true },
  noite: { start: "18:00", end: null, ligado: true },
};

export const NOME_DO_TURNO: Record<Turno, string> = { manha: "Manhã", tarde: "Tarde", noite: "Noite" };
const TURNO_NA_FRASE: Record<Turno, string> = { manha: "de manhã", tarde: "à tarde", noite: "à noite" };

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function horarioValido(s: unknown): s is string {
  return typeof s === "string" && HHMM.test(s);
}

export function ehTurno(v: unknown): v is Turno {
  return v === "manha" || v === "tarde" || v === "noite";
}

export function modoDaEncomenda(p: { orderScheduleMode?: unknown } | null | undefined): ModoEncomenda {
  const m = p?.orderScheduleMode;
  return m === "turno" || m === "data" ? m : "horario";
}

// Turnos ligados no produto, na ordem do dia. Sem lista (ou lista vazia/inválida) = os três, o padrão aprovado.
export function turnosDoProduto(p: { orderShifts?: unknown } | null | undefined): Turno[] {
  const raw = p?.orderShifts;
  if (!Array.isArray(raw)) return [...TURNOS];
  const ligados = TURNOS.filter((t) => raw.includes(t));
  return ligados.length > 0 ? ligados : [...TURNOS];
}

// Abertura mais cedo e fechamento mais tarde da semana (dias abertos), do horário de funcionamento da loja
// (stores.business_hours: {"0".."6": {enabled, ranges: [{start, end}]}} ou o formato antigo {enabled, open, close}).
// O pedaço da madrugada (00:00 até 06:00, continuação da noite anterior) não conta como abertura; faixa que passa
// da meia-noite ou vai até ela deixa o fechamento em aberto (null). Sem dia aberto, ou aberta 24 h: null.
export function faixaDeFuncionamento(businessHours: unknown): { abre: string; fecha: string | null } | null {
  if (!businessHours || typeof businessHours !== "object") return null;
  const inicios: string[] = [];
  const fins: string[] = [];
  let semFim = false;
  for (const dia of Object.values(businessHours as Record<string, unknown>)) {
    if (!dia || typeof dia !== "object") continue;
    const d = dia as { enabled?: unknown; ranges?: unknown; open?: unknown; close?: unknown };
    if (!d.enabled) continue;
    const faixas = Array.isArray(d.ranges)
      ? (d.ranges as Array<{ start?: unknown; end?: unknown }>)
      : d.open !== undefined
        ? [{ start: d.open, end: d.close }]
        : [];
    for (const f of faixas) {
      if (!horarioValido(f?.start) || !horarioValido(f?.end)) continue;
      if (f.start === "00:00" && f.end > f.start && f.end <= "06:00") continue;
      inicios.push(f.start);
      if (f.end <= f.start || f.end >= "23:59") semFim = true;
      else fins.push(f.end);
    }
  }
  if (inicios.length === 0) return null;
  const abre = inicios.sort()[0]!;
  const fecha = semFim ? null : fins.sort().at(-1) ?? null;
  if (abre === "00:00" && fecha === null) return null;
  return { abre, fecha };
}

// Turnos a partir do horário de funcionamento (a regra do Pedro): Manhã da abertura até 12h, Tarde do maior entre a
// abertura e 12h até o menor entre o fechamento e 18h, Noite do maior entre a abertura e 18h até o fechamento. Turno
// sem pedaço dentro do horário vem desligado (com o horário padrão, para quando a loja o ligar).
export function turnosDoFuncionamento(businessHours: unknown): HorarioDosTurnos | null {
  const f = faixaDeFuncionamento(businessHours);
  if (!f) return null;
  const P = HORARIO_DOS_TURNOS_PADRAO;
  const max = (a: string, b: string) => (a > b ? a : b);
  const min = (a: string, b: string) => (a < b ? a : b);
  return {
    manha: f.abre < "12:00" ? { start: f.abre, end: "12:00", ligado: true } : { ...P.manha, ligado: false },
    tarde:
      f.abre < "18:00" && (f.fecha === null || f.fecha > "12:00")
        ? { start: max(f.abre, "12:00"), end: f.fecha === null ? "18:00" : min(f.fecha, "18:00"), ligado: true }
        : { ...P.tarde, ligado: false },
    noite: f.fecha === null || f.fecha > "18:00" ? { start: max(f.abre, "18:00"), end: f.fecha, ligado: true } : { ...P.noite, ligado: false },
  };
}

// Horário EFETIVO dos turnos da loja: o que ela salvou (turno null ou ligado false = desligado); sem nada salvo, o que
// sai do horário de funcionamento; sem horário de funcionamento, o padrão.
export function horarioDosTurnos(raw: unknown, businessHours?: unknown): HorarioDosTurnos {
  if (!raw || typeof raw !== "object") return turnosDoFuncionamento(businessHours) ?? { ...HORARIO_DOS_TURNOS_PADRAO };
  const out = { ...HORARIO_DOS_TURNOS_PADRAO };
  for (const t of TURNOS) {
    const f = (raw as Record<string, unknown>)[t] as { start?: unknown; end?: unknown; ligado?: unknown } | null | undefined;
    if (f === null) {
      out[t] = { ...HORARIO_DOS_TURNOS_PADRAO[t], ligado: false };
      continue;
    }
    if (!f || typeof f !== "object") continue;
    const start = horarioValido(f.start) ? f.start : HORARIO_DOS_TURNOS_PADRAO[t].start;
    const end = horarioValido(f.end) && f.end > start ? f.end : horarioValido(f.start) ? null : HORARIO_DOS_TURNOS_PADRAO[t].end;
    out[t] = { start, end, ligado: f.ligado !== false };
  }
  return out;
}

export function turnosLigados(horarios: HorarioDosTurnos): Turno[] {
  return TURNOS.filter((t) => horarios[t].ligado);
}

// Erro de validação de um horário de turnos enviado pela loja (null = válido). Turno desligado (ligado false ou
// null) não precisa de horário; pelo menos um fica ligado.
export function erroNoHorarioDosTurnos(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return "Horário dos turnos inválido.";
  let ligados = 0;
  for (const t of TURNOS) {
    const f = (raw as Record<string, unknown>)[t] as { start?: unknown; end?: unknown; ligado?: unknown } | null | undefined;
    if (f === null || (f && typeof f === "object" && f.ligado === false)) continue;
    if (!f || typeof f !== "object") return `Informe o horário da ${NOME_DO_TURNO[t]}.`;
    if (!horarioValido(f.start)) return `${NOME_DO_TURNO[t]}: o começo precisa ser um horário (ex.: 08:00).`;
    if (f.end != null && f.end !== "") {
      if (!horarioValido(f.end)) return `${NOME_DO_TURNO[t]}: o fim precisa ser um horário (ex.: 12:00).`;
      if (f.end <= f.start) return `${NOME_DO_TURNO[t]}: o fim precisa ser depois do começo.`;
    }
    ligados++;
  }
  return ligados === 0 ? "Deixe pelo menos um turno ligado." : null;
}

function horaCurta(hhmm: string): string {
  const [h, m] = hhmm.split(":");
  return `${Number(h)}h${m && m !== "00" ? m : ""}`;
}

// "8h às 12h" · "a partir das 18h"
export function faixaDoTurnoTexto(f: { start: string; end: string | null }): string {
  return f.end ? `${horaCurta(f.start)} às ${horaCurta(f.end)}` : `a partir das ${horaCurta(f.start)}`;
}

// Data da encomenda por extenso, como sai na mensagem e no pedido: "15/10/2026 às 15:00", "15/10/2026 à tarde"
// ou só "15/10/2026". O horário vence o turno quando os dois existem (item que veio com horário na data única).
export function encomendaPorExtenso(data: string, hora?: string | null, turno?: string | null): string {
  const [y, m, d] = data.split("-");
  const dia = `${d}/${m}/${y}`;
  if (hora) return `${dia} às ${hora}`;
  if (ehTurno(turno)) return `${dia} ${TURNO_NA_FRASE[turno]}`;
  return dia;
}

// Turno que contém o horário (o primeiro na ordem do dia), entre os permitidos e ligados na loja. null = fora de todos.
export function turnoDoHorario(hora: string, horarios: HorarioDosTurnos, permitidos: readonly Turno[] = TURNOS): Turno | null {
  if (!horarioValido(hora)) return null;
  for (const t of TURNOS) {
    if (!permitidos.includes(t) || !horarios[t].ligado) continue;
    const f = horarios[t];
    if (hora >= f.start && (f.end === null || hora < f.end)) return t;
  }
  return null;
}

// O turno entra no dia quando alguma faixa de "Disponibilidade limitada" encosta nele (sábado das 8h às 12h →
// só a Manhã). Faixa invertida ou vazia não conta, como na grade de horários.
export function turnoCabeNasFaixas(f: { start: string; end: string | null }, faixas: ReadonlyArray<{ start: string; end: string }>): boolean {
  const fim = f.end ?? "24:00";
  return faixas.some((w) => w.end > w.start && w.start < fim && w.end > f.start);
}

// Começo do dia para a antecedência em horas no "só a data": a primeira faixa do dia, ou 8h (o começo da grade
// padrão) quando o produto não limita os horários.
export function inicioDoDia(faixas: ReadonlyArray<{ start: string; end: string }> | null | undefined): string {
  const validas = (faixas ?? []).filter((w) => horarioValido(w.start) && w.end > w.start).map((w) => w.start);
  return validas.length > 0 ? validas.sort()[0]! : "08:00";
}

// Chave para ordenar encomendas do mesmo dia: horário, ou o começo do turno; "" (sem horário) abre o dia.
export function ordemNoDia(hora: string | null | undefined, turno: string | null | undefined, horarios: HorarioDosTurnos): string {
  if (hora) return hora;
  if (ehTurno(turno)) return horarios[turno].start;
  return "";
}

// O modo mais detalhado entre os itens (data única no carrinho): horário > turno > data.
export function modoMaisDetalhado(modos: Iterable<ModoEncomenda>): ModoEncomenda {
  let melhor: ModoEncomenda = "data";
  for (const m of modos) {
    if (m === "horario") return "horario";
    if (m === "turno") melhor = "turno";
  }
  return melhor;
}
